import { useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/forge/Field'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { PlainBadge } from '@/components/forge/StatusBadge'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Mono, Num } from '@/components/forge/primitives'
import {
  CODE_MAX_LENGTH,
  ENTITY_TYPE_FOR_TIER,
  NAME_MAX_LENGTH,
  ROOT_ID,
  TIERS,
  TIERS_WITH_ASSET_CLASS,
  TIERS_WITH_DISPLAY_PREFS,
  TIERS_WITH_SEQUENCE,
  alwaysActive,
  childTierOf,
} from '@/features/hierarchy/api'
import { canonicalTimezone } from '@/features/auth/api'
import { useSession, useTimezones } from '@/features/auth/mutations'
import { DATE_FORMAT_KEYS, TIME_FORMATS } from '@/lib/orgFormats'
import {
  useChildCount,
  useCreateNode,
  useDeleteNode,
  useHierarchyAncestors,
  useHierarchyChildren,
  useUpdateNode,
} from '@/features/hierarchy/queries'
import { useAssetClasses } from '@/features/assetClasses/queries'
import { TablePagination } from '@/components/forge/TablePagination'
import { TierGlyph } from '@/components/forge/TierGlyph'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/lib/cn'

/**
 * Sites & Hierarchy.
 *
 * Four tiers: enterprise › site › line › asset. The tier a node can contain is
 * derived from its own tier, never chosen — that is what stops someone hanging
 * an asset directly off a site and producing a tree the rest of the platform
 * cannot scope to.
 *
 * ── It was six tiers until the backend dropped two ───────────────────────
 * Region and sub-region are gone: migration `f915c8d4b697` deleted both tables
 * and hung plants off the account. The screen went on asking `/assets/regions`
 * for its first list after that, so the drill-down began on a 404 — while the
 * estate read as EMPTY everywhere else, because `flatten.js` was walking a key
 * the response no longer carries. See `features/hierarchy/api.js`.
 *
 * ── One level at a time, not a tree ──────────────────────────────────────
 * This was a fully expanded recursive tree, and it does not survive a real
 * estate. A network of forty sites, each holding lines and each line its assets,
 * is thousands of rows rendered at once and indented to a column hundreds of
 * pixels in. The shape that reads well with one of each is precisely the shape
 * that fails at scale, which is the trap a demo dataset sets — and losing two
 * tiers does not undo that, it only lowers the exponent.
 *
 * So it drills. One list, of one tier, of one parent's children. Opening a site
 * replaces the list with its lines; the trail above says where you are and every
 * segment of it goes back. The number of rows on screen is now a function of how
 * many siblings a node has, not of how large the estate is.
 *
 * ── The position is in the URL ───────────────────────────────────────────
 * `?node=<id>`, because PRODUCT.md's standing rule is that every drill-down is
 * deep-linkable and this is now a drill-down. An administrator can send somebody
 * the line they mean instead of describing how to get to it, Back steps out a
 * level, and a reload holds position. Absent means the account root.
 *
 * An id that is not in the tree — stale link, deleted node — falls back to the
 * root rather than rendering an empty list, and says so.
 *
 * ── Live, and shaped by what the API actually accepts ────────────────────
 * The enterprise root is the account. It has no endpoint — no create, no
 * rename, no remove — so it is the one row in the tree with no controls on it.
 * It is also a site's parent now, which is why creating one sends no parent key
 * at all: the account is the token's, not a column's.
 *
 * A short code is REQUIRED at every tier and unique among its siblings, which
 * the server enforces and reports back as a field error. It used to be optional
 * here, which would now fail at submit rather than in the form.
 *
 * Every write is a full-replace PUT. That is why the editor is populated from
 * the record rather than starting empty, and why `asset_class_id` is carried
 * through even when the form never shows it: the update schema requires the
 * field, so omitting it is a rejection and sending null on a rename would
 * silently destroy a real assignment.
 *
 * ── What the server cannot store ─────────────────────────────────────────
 * The site form used to carry a city, a country and — the important one — the
 * engineering unit system, which PRODUCT.md calls a safety property and which
 * had no other home in the product. `Plant` has no column for any of them. They
 * are removed rather than collected into a field that would silently discard
 * them; the gap is written up in BACKEND-GAPS.md.
 *
 * ── Removal warns rather than refuses ────────────────────────────────────
 * This screen used to block it: a node with children could not be removed until
 * it was emptied. That is now a warning instead, by instruction — an
 * administrator restructuring a plant should not have to empty a site
 * bottom-up to retire it.
 *
 * The consequence is real and is stated twice before it happens, because the
 * server soft-deletes exactly ONE row: the children keep `deleted_at = NULL`,
 * disappear from this tree the moment their parent does, and go on holding their
 * short codes against the sibling-uniqueness check. They are not deleted and they
 * are not reachable — see BACKEND-GAPS.md item 2, which is the fix this screen is
 * working around.
 */

/**
 * One measure for this page, shared by the prose and the tree.
 *
 * They used to disagree: the intro was capped at a reading measure and the tree
 * at 52rem for its two-ended rows, which put two edges 12rem apart and made the
 * page look like a layout that had drifted rather than one that was set. `ch`
 * resolves to the same pixel width in both places because both containers
 * inherit the 14px body size.
 */
const PAGE_MEASURE = 'max-w-[74ch]'

/**
 * Rows per request.
 *
 * The API's own default, and comfortably under its ceiling of 200. Fifty
 * siblings is already past what anyone reads down — the filter is the tool for a
 * list that long — so a larger page would buy scrolling rather than answers.
 */
const PAGE_SIZE = 50

export function SitesHierarchyPage() {
  const { t } = useTranslation(['app', 'common'])
  useDocumentTitle(`${t('settings.hierarchy.title')} · ${t('common:brand.name')}`)

  /**
   * Where in the tree we are standing, as search params.
   *
   * Both the tier and the id, because the id alone does not say which endpoint
   * to read it from — five collections, five different resources, and a UUID
   * looks the same in all of them. Absent means the account root.
   *
   * `replace: false` on a drill-in is what makes Back step out a level, which is
   * the gesture people try first.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const nodeId = searchParams.get('node')
  const nodeTier = searchParams.get('tier')
  const target = nodeId && TIERS.includes(nodeTier) ? { tier: nodeTier, id: nodeId } : null

  /**
   * The path down to where we are.
   *
   * Carried while drilling, because the screen already has every node it walked
   * through and re-reading them would be five requests for facts it is holding.
   * Rebuilt from the API only on a cold arrival — a link, or a reload — which is
   * what `useHierarchyAncestors` is for.
   *
   * ── In history state, NOT in component state ────────────────────────────
   * It was a `useState`, and that was a race. Two separate updates — the trail
   * and the search params — are not guaranteed to land in the same render, so
   * there was one frame where the URL already named the new node and the trail
   * still described the old one. In that frame the page believed it had arrived
   * cold: it fired an ancestor walk, swapped the list for a skeleton, and then
   * corrected itself on the next render, aborting the requests it had just
   * issued. That is the flicker, and those are the cancelled reads.
   *
   * History state moves WITH the URL, in one navigation, so the two can never
   * disagree. It also makes Back and Forward restore the trail for the entry
   * they land on rather than the one being left — which the old version got
   * wrong in a way nobody would have noticed until they used Back.
   *
   * It does not survive a hard reload, which is correct: a pasted link has no
   * trail behind it and should take the cold path.
   */
  const location = useLocation()
  const carried = location.state?.hierarchyTrail
  const walked = Array.isArray(carried) && carried.at(-1)?.id === nodeId ? carried : null

  const ancestors = useHierarchyAncestors(target, { enabled: Boolean(target) && !walked })

  /**
   * The filter and the page, DERIVED against the node they were set for.
   *
   * Both reset on every move, and resetting them with `setState` is the same
   * race the trail had: component state and the router's location do not
   * necessarily land in one render, so there was a frame where the parent was
   * already the new one and the search term was still the old one — a request
   * issued for a combination nobody asked for, then cancelled.
   *
   * Stamped with the node they belong to instead. When the node changes they are
   * stale by identity and read as empty on the very same render, with nothing to
   * synchronise. This is the pattern `useDraft` on the General page settled on,
   * for the same reason.
   */
  const [filterState, setFilterState] = useState({ nodeId: null, value: '' })
  const filter = filterState.nodeId === nodeId ? filterState.value : ''
  const setFilter = (value) => setFilterState({ nodeId, value })

  const [pageState, setPageState] = useState({ nodeId: null, search: '', value: 1 })
  const page =
    pageState.nodeId === nodeId && pageState.search === filter ? pageState.value : 1
  const setPage = (value) => setPageState({ nodeId, search: filter, value })
  /** `{ mode: 'create'|'edit', parent?, node? }` — one editor open at a time. */
  const [editor, setEditor] = useState(null)
  /** `{ node, count }` — the record a confirmation is open for. */
  const [removing, setRemoving] = useState(null)

  /**
   * The account root.
   *
   * It has no endpoint — not to read, create, rename or remove — so its name
   * comes from the session, which already carries the organisation. That is one
   * request this page no longer makes on its own.
   */
  const { data: session } = useSession()
  const root = useMemo(
    () => ({
      id: ROOT_ID,
      tier: 'enterprise',
      parentId: null,
      name: session?.account?.name ?? '',
      code: null,
      isActive: true,
      readOnly: true,
    }),
    [session],
  )

  const trail = useMemo(
    () => [root, ...(walked ?? ancestors.data ?? [])],
    [root, walked, ancestors.data],
  )
  const current = trail.at(-1)
  const childTier = childTierOf(current.tier)

  /**
   * The rows on screen. One request, one generation, filtered and paged by the
   * server — see `fetchChildren` for why the search is not done here.
   */
  const children = useHierarchyChildren({
    tier: childTier,
    parentId: current.id === ROOT_ID ? null : current.id,
    search: filter,
    page,
    pageSize: PAGE_SIZE,
  })

  const rows = children.data?.items ?? []

  /* The count for the row we are standing in comes free with its children —
     it is what the list just reported. Child rows cannot show one: a count per
     row would be a request per row. See BACKEND-GAPS.md. */
  const currentChildCount = children.data?.total ?? 0

  const createNode = useCreateNode()
  const updateNode = useUpdateNode()
  const deleteNode = useDeleteNode()

  /* Read when an editor opens, because the removal warning has to state a real
     number and this screen holds only one generation. */
  const editingCount = useChildCount(
    editor?.mode === 'edit' && !editor.node.readOnly
      ? { tier: editor.node.tier, id: editor.node.id }
      : null,
  )

  /**
   * Move to a node.
   *
   * The trail is carried rather than recomputed: `path` is where we came from,
   * so drilling in appends and a trail click truncates. Filter and page reset on
   * every move — both were set against the list being left, and carrying either
   * into the next one presents a partial list as the whole of what is there.
   */
  function goTo(node, path) {
    setEditor(null)
    setRemoving(null)
    /* The filter and the page are not reset here: they are stamped with the node
       they belong to and go stale on their own the moment it changes. Resetting
       them by hand is what put them a render behind the URL. */

    const next = new URLSearchParams(searchParams)
    if (node && node.id !== ROOT_ID) {
      next.set('node', node.id)
      next.set('tier', node.tier)
    } else {
      next.delete('node')
      next.delete('tier')
    }
    /* The trail rides on the same navigation as the params — see above. Plain
       objects only, because history state is structured-cloned. */
    setSearchParams(next, { state: { hierarchyTrail: path ?? [] } })
  }

  /** Down one level: the trail we are on, plus the node being opened. */
  const openNode = (node) => goTo(node, [...trail.slice(1), node])

  /** Up to a trail segment: everything down to and including it. */
  const goToTrail = (node) => {
    const index = trail.findIndex((step) => step.id === node.id)
    goTo(node, trail.slice(1, index + 1))
  }

  function requestDelete(node) {
    setEditor(null)
    deleteNode.reset()
    setRemoving({ node, count: editingCount.data ?? 0 })
  }

  function openCreate(parent) {
    setRemoving(null)
    createNode.reset()
    updateNode.reset()
    setEditor({ mode: 'create', parent })
  }

  function openEdit(node) {
    setRemoving(null)
    createNode.reset()
    updateNode.reset()
    setEditor({ mode: 'edit', node })
  }

  function submitEditor(values) {
    if (editor.mode === 'create') {
      createNode.mutate(
        { parentTier: editor.parent.tier, parentId: editor.parent.id, values },
        { onSuccess: () => setEditor(null) },
      )
    } else {
      updateNode.mutate(
        { tier: editor.node.tier, id: editor.node.id, values },
        { onSuccess: () => setEditor(null) },
      )
    }
  }

  const trailPending = Boolean(target) && !walked && ancestors.isPending
  const trailFailed = Boolean(target) && !walked && ancestors.isError

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <header className={cn('mb-6', PAGE_MEASURE)}>
        {/* Display size, like every other screen that owns its page. Settings
            renders no title of its own above this, so at headline size the page
            read as a subsection of something that was not there. */}
        <h1 className="m-0 text-display">{t('settings.hierarchy.title')}</h1>
        <p className="mt-3 text-body leading-[1.6] text-ink-dim">{t('settings.hierarchy.intro')}</p>
      </header>

      {/* ── List on the left, editor on the right ────────────────────────
          The list is capped rather than let run to the full 1700px page: its
          rows are a name at one end and a short code and a control at the other,
          so a full-width row is mostly the gap between them.

          The editor is a panel beside the list rather than a modal over it.
          Creating a node is a statement about a PLACE in the hierarchy, and a
          modal hides the one thing that gives the form its meaning — which
          parent you are adding under, and what is already there. */}
      <div className="flex items-start gap-5 max-xl:flex-col">
        <div className={cn('w-full min-w-0 flex-1', PAGE_MEASURE)}>
          {trailFailed ? (
            /* NOT `ErrorState`. Its action is "try again", and it hides that
               action entirely when the error is not retryable — which a 404 from
               a stale link is not, so the reader would be left with no way out
               of a page that cannot say where it is. The way out here is to go
               somewhere else, and it is always offered. */
            <Panel className="px-6 py-9 text-center">
              <Icon name="bolt" size="lg" className="mx-auto text-red" />
              <p className="mt-3 text-body font-semibold">
                {t('settings.hierarchy.trailFailed')}
              </p>
              <p className="mx-auto mt-1.5 max-w-[52ch] text-body-sm text-ink-dim">
                {t('settings.hierarchy.trailFailedBody')}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <ForgeButton variant="primary" onClick={() => goTo(null)}>
                  {t('settings.hierarchy.backToTop')}
                </ForgeButton>
                <ForgeButton onClick={() => ancestors.refetch()}>
                  {t('common:actions.retry')}
                </ForgeButton>
              </div>
            </Panel>
          ) : trailPending ? (
            <SkeletonRows count={5} />
          ) : (
            <>
              <Trail trail={trail} onGo={goToTrail} />

              <div className="mb-3.5 flex flex-wrap items-center gap-2">
                <div className="flex min-w-[16rem] flex-1 items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-2 focus-within:border-forge">
                  <Icon name="search" size="sm" className="text-ink-mute" />
                  <label className="sr-only" htmlFor="tree-filter">
                    {t('settings.hierarchy.searchPlaceholder')}
                  </label>
                  <input
                    id="tree-filter"
                    value={filter}
                    /* Page two of the old search is not page two of this one.
                       Nothing resets it: `page` is stamped with the search it
                       was set for, so a new term reads as page 1 on the same
                       render the term changes. */
                    onChange={(event) => setFilter(event.target.value)}
                    /* Names the tier being filtered, because the filter searches
                       one generation rather than the whole tree. */
                    placeholder={
                      childTier
                        ? t('settings.hierarchy.filterTier', {
                            tier: t(`settings.hierarchy.tiersPlural.${childTier}`).toLowerCase(),
                          })
                        : t('settings.hierarchy.searchPlaceholder')
                    }
                    className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-dim"
                  />
                </div>
                {childTier ? (
                  <ForgeButton size="sm" variant="primary" onClick={() => openCreate(current)}>
                    <Icon name="plus" size="sm" />
                    {t('settings.hierarchy.addChild', {
                      tier: t(`settings.hierarchy.tiers.${childTier}`).toLowerCase(),
                      parent: current.name,
                    })}
                  </ForgeButton>
                ) : null}
              </div>

              <Panel flush className="overflow-hidden">
                {/* The node being stood in, above its children rather than as
                    one of them. It is not a sibling of the list, and drawing it
                    in line would make it read as one. */}
                <CurrentRow
                  node={current}
                  childCount={currentChildCount}
                  childTier={childTier}
                  onEdit={openEdit}
                />

                {children.isError ? (
                  <div className="p-4">
                    <ErrorState
                      error={children.error}
                      title={t('settings.hierarchy.loadFailed')}
                      onRetry={children.refetch}
                    />
                  </div>
                ) : children.isPending ? (
                  <div className="p-3">
                    <SkeletonRows count={4} />
                  </div>
                ) : rows.length ? (
                  <>
                    <ul
                      className={cn(
                        'm-0 list-none p-0 transition-opacity',
                        // Held on screen while the next page loads, dimmed so it
                        // is visibly not the answer yet. Blanking the list
                        // between pages is what makes paging feel broken.
                        children.isPlaceholderData && 'opacity-60',
                      )}
                    >
                      {rows.map((node) => (
                        <NodeRow key={node.id} node={node} onOpen={openNode} onEdit={openEdit} />
                      ))}
                    </ul>
                    {children.data.total > PAGE_SIZE ? (
                      <TablePagination
                        total={children.data.total}
                        page={page}
                        pageSize={PAGE_SIZE}
                        onPageChange={setPage}
                        label={t(`settings.hierarchy.tiersPlural.${childTier}`).toLowerCase()}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="m-0 px-4 py-6 text-center text-body-sm text-ink-dim">
                    {filter.trim()
                      ? t('settings.hierarchy.noMatch')
                      : childTier
                        ? t('settings.hierarchy.emptyTier', {
                            tier: t(`settings.hierarchy.tiersPlural.${childTier}`).toLowerCase(),
                            parent: current.name,
                          })
                        : t('settings.hierarchy.leafTier')}
                  </p>
                )}
              </Panel>
            </>
          )}
        </div>

        {/* Sticky, so a long list can be scrolled while the form stays put.
            Below xl it stops being a column and becomes a block above the list,
            where a 400px aside would leave neither side usable. */}
        {editor ? (
          <aside className="sticky top-6 w-[25rem] shrink-0 max-xl:static max-xl:order-first max-xl:w-full">
            <NodePanel
              editor={editor}
              pending={createNode.isPending || updateNode.isPending}
              error={editor.mode === 'create' ? createNode.error : updateNode.error}
              /* Read for this record when the form opened. `undefined` while it
                 is in flight, which is what lets the warning hold its tongue
                 rather than claim zero. */
              childCount={editingCount.data}
              onClose={() => setEditor(null)}
              onDelete={requestDelete}
              onSubmit={submitEditor}
            />
          </aside>
        ) : null}
      </div>

      <DeleteDialog
        target={removing}
        pending={deleteNode.isPending}
        error={deleteNode.error}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          deleteNode.mutate(
            { tier: removing.node.tier, id: removing.node.id },
            {
              onSuccess: () => {
                setRemoving(null)
                /* Standing inside what was just removed would leave the page on
                   a node that no longer exists. Step out to its parent. */
                if (removing.node.id === current.id) {
                  goTo(trail.at(-2) ?? root, trail.slice(1, -1))
                }
              },
            },
          )
        }}
      />
    </div>
  )
}

/* ══ DIALOGS ═══════════════════════════════════════════════════════════ */

/**
 * Create and edit, in the panel beside the tree.
 *
 * ── A panel, not a modal ──────────────────────────────────────────────────
 * Creating a node is a statement about a PLACE in the hierarchy — this line, on
 * that site. A modal covers the one thing that gives the form its meaning: which
 * parent is being added under and what is already there. Beside the tree, the
 * answer stays legible while the form is filled, and a mistaken parent is visible
 * before the write rather than after it.
 *
 * ── Edit reads the record it is about to overwrite ────────────────────────
 * `/assets/hierarchy` serves id, name and short code. `is_active` and
 * `asset_class_id` are not in it, and every update in this API is a full replace —
 * so a form prefilled from the tree alone would switch the record off and unassign
 * its class the moment it saved. One extra read, only when a form opens.
 */
function NodePanel({ editor, pending, error, childCount, onClose, onDelete, onSubmit }) {
  const { t } = useTranslation(['app', 'common'])

  const isCreate = editor.mode === 'create'
  const tier = isCreate ? childTierOf(editor.parent.tier) : editor.node.tier
  const tierLabel = t(`settings.hierarchy.tiers.${tier}`).toLowerCase()

  /**
   * No second read.
   *
   * This used to fetch the record before showing the form, because the tree
   * endpoint served only id, name and short code — and every update here is a
   * full replace, so a form prefilled from the tree would switch the record off
   * and unassign its class on save.
   *
   * The list is the collections now, and they serve the whole record:
   * `is_active`, `asset_class_id`, a plant's display preferences, a machine's
   * `sequence`. The row that opened this form already holds everything the form
   * has to send back, so the read was asking for what it had.
   */

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="m-0 text-title">
            {isCreate
              ? t('settings.hierarchy.createTitle', { tier: tierLabel })
              : t('settings.hierarchy.editTitle', { name: editor.node.name })}
          </h2>
          {isCreate ? (
            <p className="mt-1 mb-0 text-body-sm text-ink-dim">
              {t('settings.hierarchy.createUnder', { parent: editor.parent.name })}
            </p>
          ) : null}
        </div>
        <IconButton
          bare
          label={t('common:actions.close')}
          onClick={onClose}
          className="-me-1 size-7 shrink-0"
        >
          <Icon name="x" size="sm" />
        </IconButton>
      </div>

      <NodeEditor
        /* Re-keyed per record: NodeEditor seeds its fields in `useState`
           initialisers, which run once, so switching from one node to another
           without this would keep the first one's values. */
        key={isCreate ? `new-${editor.parent.id}` : editor.node.id}
        editor={editor}
        tier={tier}
        pending={pending}
        error={error}
        childCount={childCount}
        onClose={onClose}
        onDelete={onDelete}
        onSubmit={onSubmit}
      />
    </Panel>
  )
}

/**
 * The confirmation, and the second statement of the consequence.
 *
 * It used to have two modes: confirm an empty record, refuse a full one. The
 * refusal is gone — a full branch is now removable and the dialog's job is to make
 * sure nobody does it by accident. The warning is repeated here rather than
 * assumed read in the panel behind, because this is the click that cannot be taken
 * back.
 */
function DeleteDialog({ target, pending, error, onClose, onConfirm }) {
  const { t } = useTranslation(['app', 'common'])
  if (!target) return null

  const nested = target.count > 0

  return (
    <ForgeDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="sm"
      title={t('settings.hierarchy.deleteTitle', { name: target.node.name })}
      footer={
        <>
          <ForgeButton variant="danger" disabled={pending} onClick={onConfirm}>
            {t('settings.hierarchy.deleteConfirm')}
          </ForgeButton>
          <ForgeButton onClick={onClose}>{t('settings.hierarchy.cancel')}</ForgeButton>
        </>
      }
    >
      {error ? <WriteFailed error={error} className="mb-4" /> : null}

      {/* Amber and striped, the same shape `WriteFailed` uses — but amber, because
          nothing has gone wrong yet. It is describing what this action will do. */}
      {nested ? (
        <div className="mb-4 rounded-sm border border-hairline stripe-amber bg-card p-3.5">
          <p className="m-0 text-body-sm text-on-amber">
            {t('settings.hierarchy.deleteNested', {
              name: target.node.name,
              count: target.count,
            })}
          </p>
        </div>
      ) : null}

      <p className="m-0 text-body text-ink-dim">{t('settings.hierarchy.deleteBody')}</p>
    </ForgeDialog>
  )
}

/**
 * A failed write, stated where it was attempted.
 *
 * The server's own wording is never rendered: it is untranslated and can
 * describe internals. Field-level complaints are routed to their control; this
 * carries everything else.
 */
function WriteFailed({ error, className }) {
  const { t } = useTranslation(['app', 'common'])
  return (
    <div
      role="alert"
      className={cn('rounded-sm border border-hairline stripe-red bg-card p-3.5', className)}
    >
      <p className="m-0 text-control text-on-red">{t('settings.hierarchy.writeFailed')}</p>
      <p className="mt-1.5 mb-0 text-body-sm text-ink-dim">{t(`common:${error.messageKey}`)}</p>
      {error.requestId ? (
        <p className="mono mt-2 mb-0 text-micro font-normal text-ink-dim">
          {t('common:errors.requestId', { id: error.requestId })}
        </p>
      ) : null}
    </div>
  )
}

/* ══ TREE ══════════════════════════════════════════════════════════════ */

/**
 * Where you are, and every step back out.
 *
 * Buttons rather than text, all the way along — the segment you want is the one
 * you point at, and making only the last-but-one clickable turns "back three
 * levels" into three clicks. The current node is the exception: it is `aria-current`
 * and inert, because navigating to where you already are is not an action.
 *
 * It wraps rather than scrolls or truncates. Four tiers of real names still will
 * not fit on one line at a narrow width, and a path that hides its own middle is
 * the one thing this control must not do.
 */
function Trail({ trail, onGo }) {
  const { t } = useTranslation(['app', 'common'])
  if (trail.length < 2) return null

  return (
    <nav aria-label={t('settings.hierarchy.trailLabel')} className="mb-3">
      <ol className="m-0 flex list-none flex-wrap items-center gap-x-1 gap-y-1 p-0">
        {trail.map((node, index) => {
          const isCurrent = index === trail.length - 1
          return (
            <li key={node.id} className="flex items-center gap-1">
              {index > 0 ? (
                <Icon name="right" size="xs" className="mirror-rtl text-ink-mute" />
              ) : null}
              {isCurrent ? (
                <span
                  aria-current="page"
                  className="flex items-center gap-1.5 text-body-sm font-semibold text-ink"
                >
                  <TierGlyph tier={node.tier} bare />
                  {node.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onGo(node)}
                  className="flex cursor-pointer items-center gap-1.5 rounded-sm px-1 py-0.5 text-body-sm text-ink-dim transition-colors hover:bg-sunken hover:text-ink"
                >
                  <TierGlyph tier={node.tier} bare />
                  {node.name}
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * The node being stood in, at the head of its own children.
 *
 * Tinted, so it reads as the container rather than as the first row of the list.
 * The whole thing is the edit target for the same reason the child rows are —
 * except at the account root, which the API serves no way to rename or remove,
 * so there it is text rather than a control that leads to a form with nothing to
 * save.
 */
function CurrentRow({ node, childCount, childTier, onEdit }) {
  const { t } = useTranslation(['app', 'common'])

  const facts = <RowFacts node={node} childCount={childCount} childTier={childTier} />

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-hairline bg-sunken px-3 py-2">
      {node.readOnly ? (
        <span className="flex min-w-0 flex-1 items-center gap-2">{facts}</span>
      ) : (
        /* The whole row is the target, and the pencil rides inside it as a plain
           glyph rather than as a second button. On a child row the two are
           genuinely different acts — the name drills in, the pencil edits — so
           they are two controls. Here there is nowhere to drill, so a pencil
           button beside a row button would be two controls for one result, and a
           screen reader would announce the same action twice. A `<span>` inside
           the button gives sighted readers the affordance and adds nothing to
           announce. */
        <button
          type="button"
          onClick={() => onEdit(node)}
          aria-label={t('settings.hierarchy.edit', { name: node.name })}
          className="group/row flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm text-start"
        >
          {facts}
          {/* Decorative, so it follows the row's own hover and focus rather than
              carrying state of its own. */}
          <span className="flex size-7 shrink-0 items-center justify-center text-ink-mute opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100">
            <Icon name="pencil" size="sm" />
          </span>
        </button>
      )}

      {/* Empty, and deliberately so: it is the width of the child rows' chevron
          slot, which is what puts this row's pencil in the same column as
          theirs. Without it the container's pencil sits 20px further out and the
          two read as unrelated controls. */}
      <span className="flex size-5 shrink-0" aria-hidden="true" />
    </div>
  )
}

/**
 * One child, as a row that does two different things.
 *
 * ── Two targets, because they are two intentions ─────────────────────────
 * Opening a site and editing a site are not the same act, and a row that
 * did both on one click would have to guess. The name is the drill-in — the
 * thing you point at when you mean "show me what is in here" — and a pencil at
 * the far end opens the form. On a leaf, which contains nothing, the name is the
 * edit target instead, because there is nowhere to go.
 *
 * Real `<button>`s, siblings rather than nested: a button inside a button is
 * invalid markup that no browser agrees on and screen readers announce twice.
 * Each earns keyboard operation, focus-visible and Enter/Space on its own.
 */
function NodeRow({ node, onOpen, onEdit }) {
  const { t } = useTranslation(['app', 'common'])
  const childTier = childTierOf(node.tier)
  const canOpen = Boolean(childTier)

  return (
    <li>
      <div className="group flex flex-wrap items-center gap-2 border-b border-subtle px-3 py-1.5 hover:bg-sunken">
        <button
          type="button"
          onClick={() => (canOpen ? onOpen(node) : onEdit(node))}
          aria-label={
            canOpen
              ? t('settings.hierarchy.open', { name: node.name })
              : t('settings.hierarchy.edit', { name: node.name })
          }
          className="group/row flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm text-start"
        >
          <RowFacts node={node} childTier={childTier} />

          {/* A LEAF's pencil rides inside the row button, as a plain glyph.
              There is nowhere to drill from an asset, so the row itself is the
              edit target — and a pencil BUTTON beside it would be a second
              control for the same result, announced twice. Without any pencil at
              all, though, an asset row showed nothing on hover and read as
              inert, which is what this fixes. Containers get the real button in
              the slot below, because there the two are different acts. */}
          {canOpen ? null : (
            <span className="flex size-7 shrink-0 items-center justify-center text-ink-mute opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100">
              <Icon name="pencil" size="sm" />
            </span>
          )}
        </button>

        {/* The container's edit control. A leaf has its own, inside the row
            button above, so this slot is empty there rather than closing up —
            which is what keeps the chevrons in a true column instead of stepping
            in by 28px on the last row.

            Revealed on hover, by instruction — the rows read as a cleaner list
            without a pencil repeated down every one of them. `focus-visible`
            keeps it, which is not a hover question: without it a keyboard user
            tabs onto a control nobody can see.

            The trade to know about: hover does not exist on a touch screen, and
            this product is used on tablets on a plant floor. Nothing becomes
            unreachable — tapping a leaf row opens its form, and tapping a
            container drills in, where its own header row is the edit target —
            but on touch the pencil is never the route. */}
        <span className="flex size-7 shrink-0 items-center justify-center">
          {canOpen ? (
            <IconButton
              bare
              label={t('settings.hierarchy.edit', { name: node.name })}
              onClick={() => onEdit(node)}
              className="size-7 text-ink-mute opacity-0 transition-opacity hover:text-ink group-hover:opacity-100 focus-visible:opacity-100"
            >
              <Icon name="pencil" size="sm" />
            </IconButton>
          ) : null}
        </span>

        {/* Points the way in, and only where there is a way in. Not a button:
            the row's own target already does this, and a second control for the
            same result is one more thing to tab past. */}
        <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden="true">
          {canOpen ? (
            <Icon name="right" size="sm" className="mirror-rtl text-ink-mute" />
          ) : null}
        </span>
      </div>
    </li>
  )
}

/**
 * What a row says about its node: name, out-of-service badge, short code, and how
 * many children it holds.
 *
 * Extracted only because it is rendered inside a `<button>` on every row except
 * the account root, which is plain text — and the two must not be allowed to
 * drift apart.
 *
 * `childCount` is optional, and on a child row it is absent.
 *
 * The tree read used to supply every node, so a row could count its own
 * children. The collections do not report one — there is no `child_count` on any
 * of the five — and asking per row would be a request per row. So only the row
 * being stood in shows a count, because that number is what the list under it
 * just reported. Recorded in BACKEND-GAPS.md: one field on each `*Out` would
 * give every row its count back for nothing.
 */
function RowFacts({ node, childCount, childTier }) {
  const { t } = useTranslation('app')

  return (
    <>
      {/* Inside the pressable region rather than beside it, so the glyph is part
          of the target that opens this record and not a decoration sitting next to
          one. It is what identifies the tier on the row — the key strip at the top
          of the tree teaches these six once and every row then carries the mark
          alone. */}
      <TierGlyph tier={node.tier} />

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-body-sm font-semibold',
            // `=== false`, never falsiness: `/assets/hierarchy` does not serve
            // `is_active`, so the flat list carries null for it and testing
            // truthiness would dim every row in the tree.
            node.isActive === false && 'text-ink-dim',
          )}
        >
          {node.name}
        </span>
      </span>

      {/* Not a severity — a record that is switched off is not a machine that is
          broken. Neutral, and labelled rather than left to a colour. */}
      {node.isActive === false ? <PlainBadge>{t('settings.hierarchy.inactive')}</PlainBadge> : null}

      {node.code ? <Mono className="text-ink-dim max-lg:hidden">{node.code}</Mono> : null}

      {childCount > 0 ? (
        <Num className="text-micro font-normal text-ink-dim">
          {t('settings.hierarchy.childCount', {
            count: childCount,
            tier: t(`settings.hierarchy.tiers.${childTier}`).toLowerCase(),
          })}
        </Num>
      ) : null}
    </>
  )
}

/**
 * The tier as a glyph, in the tier's own hue.
 *
 * The hue ladder is violet enterprise, green site, ack line, neutral asset —
 * carried by the stroke rather than by a 28px tinted disc behind it. Coloured
 * circles down the left edge of every row read as status lights on records that
 * have no status, and each one set the row's height on its own.
 *
 * `ON_TINT_CLASS` rather than the raw hue: these are strokes on the card, and a
 * semantic hue is a fill value, not automatically a stroke value (DESIGN.md, The
 * Tint-Text Rule).
 */
/* ── The tier key is gone ─────────────────────────────────────────────────
   It was a strip inside the card, above the first row: the tiers in order,
   each with its glyph, separated by arrows — a legend teaching the ladder once
   so every row below could carry its mark alone. Five versions of it are
   recorded in the history of this file, all answering the same question: where
   does a reference belong on a screen whose subject is underneath it.

   The drill-down answered it by making the reference unnecessary. The trail
   above the card is the same tiers, in the same order, with the same glyphs,
   separated by the same arrows — except its segments carry the account's real
   names instead of the generic ones. Two rows of that, stacked, read as one
   control rendered twice, and the reader has to compare them to work out that
   they are not.

   Nothing was lost with it. The glyph is taught on the trail, beside the name it
   belongs to, which is a better place to learn it than a legend. And the tier of
   the list below is named twice already, in words: the filter says "Filter these
   assets" and the button says "Add asset under Production line 1".

   Note the glyph on a row is now uniform — a one-tier list has one tier — so it
   anchors the row rather than distinguishing it. That is worth keeping; it is
   not worth a second header strip to explain.
   ─────────────────────────────────────────────────────────────────────── */


/* ══ EDITOR ════════════════════════════════════════════════════════════ */

/**
 * Create and edit share one form. The tier is never a field: on create it is
 * derived from the parent, and on edit it cannot change — moving a node between
 * tiers is a different operation with different consequences and it does not
 * belong behind a select nobody expects to reshape their plant.
 */
function NodeEditor({ editor, tier, pending, error, childCount, onClose, onDelete, onSubmit }) {
  const { t } = useTranslation(['app', 'common'])
  const isCreate = editor.mode === 'create'
  const node = editor.node
  // Allowed, and warned about: the server soft-deletes exactly one row and leaves
  // its children listed with a parent that no longer exists.
  const hasChildren = childCount > 0
  const takesAssetClass = TIERS_WITH_ASSET_CLASS.has(tier)

  const [name, setName] = useState(isCreate ? '' : (node.name ?? ''))
  const [code, setCode] = useState(isCreate ? '' : (node.code ?? ''))
  /**
   * Whether this form shows an in-service control at all.
   *
   * Nothing is CREATED out of service — no create body declares `is_active`, on
   * any tier. A site is made in service and deactivated later.
   *
   * So the box appears on an edit and never on a create. It used to turn on the
   * tier as well, because region and sub-region had no such column; both tiers
   * are gone, so `alwaysActive` now answers only for the account root, which has
   * no form. `toBody` enforces the same rule on the way out; this is the half
   * that stops the form offering a choice it cannot send.
   */
  const showActive = !isCreate && !alwaysActive(tier)
  const [isActive, setIsActive] = useState(isCreate ? true : (node.isActive ?? true))
  const [assetClassId, setAssetClassId] = useState(isCreate ? '' : (node.assetClassId ?? ''))
  const [errors, setErrors] = useState({})

  const takesDisplayPrefs = TIERS_WITH_DISPLAY_PREFS.has(tier)
  const takesSequence = TIERS_WITH_SEQUENCE.has(tier)

  /* The zones the API will accept, which is not the browser's list — see
     `canonicalTimezone`. Fetched only for the one tier that has the fields. */
  const { data: zones = [] } = useTimezones({ enabled: takesDisplayPrefs })
  const { data: session } = useSession()

  /**
   * A new site starts from whoever is creating it.
   *
   * The three display fields are required and a site has nothing to inherit
   * them from — the product holds no organisation-level set. So a create form
   * has to put something in them, and the person setting up the site is the best
   * available guess at its clock: they are far more likely to be at or near it
   * than not. The controls are visible and editable, so a guess is a starting
   * point rather than a decision made on somebody's behalf.
   *
   * On edit it is the record's own value, reconciled against the list the same
   * way the profile form does it — a stored alias like `Asia/Calcutta` would
   * otherwise render a blank select and be refused on save.
   */
  const [timezone, setTimezone] = useState(() =>
    canonicalTimezone(isCreate ? session?.user?.timezone : node.timezone, zones),
  )
  const [dateFormat, setDateFormat] = useState(
    (isCreate ? session?.user?.dateFormat : node.dateFormat) ?? 'dmy',
  )
  const [timeFormat, setTimeFormat] = useState(
    (isCreate ? session?.user?.timeFormat : node.timeFormat) ?? 'h24',
  )
  /* A string, because the input is one. Converted at submit — and `''` is a real
     answer here, meaning unpositioned, which is `null` on the wire. */
  const [sequence, setSequence] = useState(
    isCreate ? '' : (node.sequence == null ? '' : String(node.sequence)),
  )

  // Only fetched for the three tiers that can carry one — which is every tier
  // below the account. The root has no form, so it never asks.
  const { data: classData } = useAssetClasses({ enabled: takesAssetClass })
  const entityType = ENTITY_TYPE_FOR_TIER[tier]
  const classOptions = useMemo(() => {
    const rows = (classData?.items ?? []).filter((row) => row.entityType === entityType)
    return [
      { value: '', label: t('settings.hierarchy.fields.noAssetClass') },
      ...rows.map((row) => ({ value: row.id, label: row.name })),
    ]
  }, [classData, entityType, t])

  /**
   * A field the server rejected outranks the local check — it is the authority
   * on what it just refused, and the only thing that knows a sibling already
   * holds this code. Its wording is not rendered; this copy says it instead.
   */
  const serverFields = error?.fields ?? {}
  const nameError =
    errors.name ?? (serverFields.name ? t('settings.hierarchy.fields.nameRejected') : null)
  const codeError =
    errors.code ?? (serverFields.short_code ? t('settings.hierarchy.fields.codeTaken') : null)
  /* A position is unique along its line, which only the server can know — no
     count the form holds would tell it whether a sibling already sits at 3. */
  const sequenceError =
    errors.sequence ?? (serverFields.sequence ? t('settings.hierarchy.fields.sequenceTaken') : null)
  const timezoneError =
    errors.timezone ?? (serverFields.timezone ? t('settings.hierarchy.fields.timezoneRejected') : null)

  /**
   * Every field the server can name, so the banner and the control never both
   * report the same rejection. Listed rather than derived: a refusal this form
   * has no control for must still reach the reader, and falling through to the
   * banner is how it does.
   */
  const routedToAField =
    serverFields.name || serverFields.short_code || serverFields.sequence || serverFields.timezone

  function submit(event) {
    event.preventDefault()
    const next = {}
    if (!name.trim()) next.name = t('settings.hierarchy.fields.nameRequired')
    // Optional here until the API made it a requirement at every tier. A form
    // that still called it optional would be a rejection waiting to happen.
    if (!code.trim()) next.code = t('settings.hierarchy.fields.codeRequired')
    // Required on a plant with no default and nothing to inherit from, so a
    // blank one is refused here rather than as a 422 naming a field nobody saw.
    if (takesDisplayPrefs && !timezone) {
      next.timezone = t('settings.hierarchy.fields.timezoneRequired')
    }
    setErrors(next)
    if (Object.keys(next).length) return

    onSubmit({
      name,
      code,
      /* Dropped by `toBody` on a create, because no create schema declares the
         field. Passed as it stands otherwise. */
      isActive,
      // Carried through even when the form never showed it: the update schema
      // requires the field, and sending null on a rename would wipe a real
      // assignment made somewhere else.
      assetClassId: takesAssetClass ? assetClassId : null,
      // Site only. `toBody` drops them for every other tier.
      timezone,
      dateFormat,
      timeFormat,
      // Asset only. Empty means unpositioned, which is null on the wire — and
      // `Number('')` is 0, a real position, so the check comes first.
      sequence: sequence.trim() === '' ? null : Number(sequence),
    })
  }

  // Escape is the dialog's job — Radix owns it, along with the focus trap and
  // restoring focus to the row that opened this.
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {error && !routedToAField ? <WriteFailed error={error} /> : null}

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(13rem,1fr))]">
        <Field
          id="node-name"
          label={t('settings.hierarchy.fields.name')}
          required
          // The form opened because someone chose to name something. Put the
          // caret where they are already about to type.
          autoFocus
          maxLength={NAME_MAX_LENGTH}
          value={name}
          error={nameError}
          onChange={(event) => setName(event.target.value)}
        />

        <Field
          id="node-code"
          label={t('settings.hierarchy.fields.code')}
          hint={t('settings.hierarchy.fields.codeHint')}
          required
          maxLength={CODE_MAX_LENGTH}
          value={code}
          error={codeError}
          onChange={(event) => setCode(event.target.value)}
          inputClassName="mono uppercase"
          autoCapitalize="characters"
          spellCheck={false}
        />
      </div>

      {takesAssetClass ? (
        <ForgeSelect
          id="node-asset-class"
          label={t('settings.hierarchy.fields.assetClass')}
          hint={t('settings.hierarchy.fields.assetClassHint')}
          value={assetClassId}
          onValueChange={setAssetClassId}
          options={classOptions}
        />
      ) : null}

      {/* ── A site's own clock ────────────────────────────────────────────
          The site is the only tier with these, and they are required on every
          write. They belong here rather than on an organisation: a network with
          plants in two countries has two working days, and a shift boundary read
          in the wrong zone is read on the wrong date. This is the same trio the
          signed-in person holds for themselves — a plant's is the site's local
          time, not the reader's. */}
      {takesDisplayPrefs ? (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]">
          <ForgeSelect
            id="node-timezone"
            label={t('settings.hierarchy.fields.timezone')}
            hint={
              timezone
                ? t('settings.hierarchy.fields.timezoneHint')
                : t('settings.hierarchy.fields.timezoneUnknown')
            }
            required
            value={timezone ?? ''}
            onValueChange={setTimezone}
            error={timezoneError}
            /* An IANA identifier is a machine string — the same in every locale
               — so it is printed rather than translated. */
            options={zones.map((zone) => ({ value: zone, label: zone }))}
          />
          <ForgeSelect
            id="node-date-format"
            label={t('settings.hierarchy.fields.dateFormat')}
            value={dateFormat}
            onValueChange={setDateFormat}
            options={DATE_FORMAT_KEYS.map((key) => ({
              value: key,
              label: t(`settings.general.dateFormats.${key}`),
            }))}
          />
          <ForgeSelect
            id="node-time-format"
            label={t('settings.hierarchy.fields.timeFormat')}
            value={timeFormat}
            onValueChange={setTimeFormat}
            options={TIME_FORMATS.map((key) => ({
              value: key,
              label: t(`settings.general.timeFormats.${key}`),
            }))}
          />
        </div>
      ) : null}

      {/* Where this asset sits in its line's running order. Optional — an
          unpositioned machine is a real state, not a missing answer — so the
          hint says what leaving it blank means rather than the label implying a
          number is owed. */}
      {takesSequence ? (
        <Field
          id="node-sequence"
          label={t('settings.hierarchy.fields.sequence')}
          hint={t('settings.hierarchy.fields.sequenceHint')}
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          value={sequence}
          error={sequenceError}
          onChange={(event) => setSequence(event.target.value)}
          inputClassName="tnum max-w-[10rem]"
        />
      ) : null}

      {/* Not a delete. A site, line or asset taken out of service keeps its
          history and its identity — it simply stops being current.

          Still the most destructive control on this form: the server hides
          everything beneath a deactivated node, so clearing it on a site hides
          that site's lines and assets everywhere in the product. It used to be
          offered on a region too, where it emptied far more; those tiers are
          gone. See `alwaysActive`. */}
      {showActive ? (
        <label className="flex cursor-pointer items-start gap-2.5 rounded-sm border border-hairline bg-card px-3.5 py-3">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            className="mt-0.5 accent-forge"
          />
          <span>
            <span className="block text-control text-ink">
              {t('settings.hierarchy.fields.active')}
            </span>
            <span className="mt-1 block text-body-sm text-ink-dim">
              {t('settings.hierarchy.fields.activeHint')}
            </span>
          </span>
        </label>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-1">
        <ForgeButton type="submit" variant="primary" disabled={pending}>
          {isCreate ? t('settings.hierarchy.create') : t('settings.hierarchy.save')}
        </ForgeButton>
        <ForgeButton type="button" onClick={onClose}>
          {t('settings.hierarchy.cancel')}
        </ForgeButton>
      </div>

      {/* ── Removal lives here, below a rule, and only on an existing record ──
          It used to be a trash icon on every row, one tab stop away from the add
          button and two pixels from the pencil — a destructive action at the same
          weight and the same size as the two safe ones beside it, repeated down
          the tree. Here it is named in words, separated by a hairline, and reached
          only from the form for the specific thing it would delete.

          A node with children is removable, and the consequence is stated BEFORE
          the click rather than discovered after it — the children are not deleted,
          they are cut loose. Amber, not red: nothing has failed, this is describing
          what the action will do. The dialog says it again on the way through,
          because that is the click that cannot be undone. */}
      {isCreate ? null : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-subtle pt-4">
          {hasChildren ? (
            <p id="remove-nested" className="m-0 max-w-[24rem] text-body-sm text-on-amber">
              {t('settings.hierarchy.deleteNested', { name: node.name, count: childCount })}
            </p>
          ) : null}
          <ForgeButton
            type="button"
            variant="danger"
            className="ms-auto"
            disabled={pending}
            aria-describedby={hasChildren ? 'remove-nested' : undefined}
            onClick={() => onDelete(node)}
          >
            <Icon name="trash" size="sm" />
            {t('settings.hierarchy.remove')}
          </ForgeButton>
        </div>
      )}
    </form>
  )
}
